import React from "react";
import dayjs from "dayjs";
import {
  Flex, Spinner, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr, useColorModeValue,
} from "@chakra-ui/react";
import Card from "horizon/components/card/Card";
import ReservationStatusChip from "./ReservationStatusChip";
import { eventTypeLabel } from "constants/eventTypeLabels";
import { pickArtistLabel, pickAuditorLabel } from "./reservationsManageConstants";
import { HEAD_CELL, BODY_CELL, ROW } from "./reservationsManageStyles";

const COLUMNS = ["Statut", "Événement", "Date", "Artiste", "Auditor", "Montant"];

/**
 * Formats an ISO date as DD/MM/YYYY HH:mm or "—" when missing/invalid.
 * @param {string} value - ISO date string
 * @returns {string}
 */
function formatDate(value) {
  if (!value) return "—";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("DD/MM/YYYY HH:mm") : "—";
}

/**
 * Event cell: title + event type subtitle.
 * @param {Object} props - reservation object
 * @returns {React.ReactElement}
 */
function EventCell({ reservation }) {
  const title = useColorModeValue("secondaryGray.900", "whiteAlpha.900");
  const sub = useColorModeValue("secondaryGray.600", "secondaryGray.400");
  return (
    <Flex direction="column" minW="0">
      <Text noOfLines={1} fontSize="sm" fontWeight="600" color={title}>{reservation?.title || "—"}</Text>
      <Text noOfLines={1} fontSize="xs" color={sub}>{eventTypeLabel(reservation?.event) || "—"}</Text>
    </Flex>
  );
}

/**
 * Auditor cell: name + email below.
 * @param {Object} props - reservation object
 * @returns {React.ReactElement}
 */
function AuditorCell({ reservation }) {
  const auditor = pickAuditorLabel(reservation);
  const name = useColorModeValue("secondaryGray.900", "whiteAlpha.900");
  const sub = useColorModeValue("secondaryGray.600", "secondaryGray.400");
  return (
    <Flex direction="column" minW="0">
      <Text noOfLines={1} fontSize="sm" fontWeight="500" color={name}>{auditor.name}</Text>
      {auditor.email && <Text noOfLines={1} fontSize="xs" color={sub}>{auditor.email}</Text>}
    </Flex>
  );
}

/**
 * Renders a single reservation row.
 * @param {Object} props - reservation, onSelect
 * @returns {React.ReactElement}
 */
function ReservationRow({ reservation, onSelect }) {
  const color = useColorModeValue("secondaryGray.900", "whiteAlpha.900");
  const hoverBg = useColorModeValue("secondaryGray.100", "whiteAlpha.100");
  return (
    <Tr {...ROW} _hover={{ bg: hoverBg }} onClick={() => onSelect?.(reservation)}>
      <Td {...BODY_CELL}><ReservationStatusChip status={reservation?.status} /></Td>
      <Td {...BODY_CELL}><EventCell reservation={reservation} /></Td>
      <Td {...BODY_CELL} color={color}>{formatDate(reservation?.event_date)}</Td>
      <Td {...BODY_CELL} color={color}>{pickArtistLabel(reservation)}</Td>
      <Td {...BODY_CELL}><AuditorCell reservation={reservation} /></Td>
      <Td {...BODY_CELL} color={color}>{reservation?.total_amount != null ? reservation.total_amount : "—"}</Td>
    </Tr>
  );
}

/**
 * Centered state message (loading spinner / error / empty).
 * @param {Object} props - loading (boolean), children (message node)
 * @returns {React.ReactElement}
 */
function StateBox({ loading, children }) {
  return <Flex align="center" justify="center" py="40px">{loading ? <Spinner color="brand.500" /> : children}</Flex>;
}

/**
 * Reservations table — handles loading / error / empty internally.
 * @param {Object} props - items, loading, error, onSelect
 * @returns {React.ReactElement}
 */
function ReservationsTable({ items, loading, error, onSelect }) {
  const empty = !items || items.length === 0;
  const headColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
  const errColor = useColorModeValue("red.500", "red.300");
  const emptyColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
  return (
    <Card px="0" py="0" overflow="hidden">
      {loading && empty ? <StateBox loading><span /></StateBox> : null}
      {!loading && error && empty ? (
        <StateBox><Text color={errColor} fontSize="sm">{typeof error === "string" ? error : "Erreur lors du chargement"}</Text></StateBox>
      ) : null}
      {!loading && !error && empty ? (
        <StateBox><Text color={emptyColor} fontSize="sm">Aucune réservation à afficher</Text></StateBox>
      ) : null}
      {!empty ? (
        <TableContainer>
          <Table variant="simple" size="sm">
            <Thead>
              <Tr>{COLUMNS.map((c) => <Th key={c} {...HEAD_CELL} color={headColor}>{c}</Th>)}</Tr>
            </Thead>
            <Tbody>
              {items.map((r) => <ReservationRow key={r.id} reservation={r} onSelect={onSelect} />)}
            </Tbody>
          </Table>
        </TableContainer>
      ) : null}
    </Card>
  );
}

export default ReservationsTable;
